// src/components/FilterBar.tsx
// Search input + active filter chips + add/clear controls.

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { cn } from '@core/ui';

import { UserPicker } from '@/components/UserPicker';

import type { FilterValue, FilterConfig } from '@/features/workspace/types';

// ---------------------------------------------------------------------------
// Inline SVG icons
// ---------------------------------------------------------------------------

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function FilterIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Value readers — pull the current value of a field out of the active filters
// ---------------------------------------------------------------------------

function arrayValue(filters: FilterValue[], field: string): string[] {
  const existing = filters.find((f) => f.field === field);
  if (!existing) return [];
  return Array.isArray(existing.value) ? (existing.value as string[]) : [String(existing.value)];
}

function firstValue(filters: FilterValue[], field: string): string {
  return arrayValue(filters, field)[0] ?? '';
}

function rangeValue(filters: FilterValue[], field: string): [string, string] {
  const existing = filters.find((f) => f.field === field);
  if (existing && existing.operator === 'between' && Array.isArray(existing.value)) {
    const [from, to] = existing.value as [string, string];
    return [from ?? '', to ?? ''];
  }
  return ['', ''];
}

// ---------------------------------------------------------------------------
// Inline editors for filter kinds that have no fixed option list
// ---------------------------------------------------------------------------

interface DateRangeEditorProps {
  initial: [string, string];
  onApply: (from: string, to: string) => void;
  onClear: () => void;
}

function DateRangeEditor({ initial, onApply, onClear }: DateRangeEditorProps) {
  const { t } = useI18n();
  const [from, setFrom] = useState(initial[0]);
  const [to, setTo] = useState(initial[1]);

  return (
    <div className="p-2 space-y-2">
      <label className="block text-xs font-medium text-muted-foreground">
        {t('workspace.filter.dateFrom')}
        <input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </label>
      <label className="block text-xs font-medium text-muted-foreground">
        {t('workspace.filter.dateTo')}
        <input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </label>
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={() => onApply(from, to)}
          className="flex-1 rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          {t('workspace.filter.apply')}
        </button>
        <button
          type="button"
          onClick={onClear}
          className="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          {t('common.clear')}
        </button>
      </div>
    </div>
  );
}

interface TextValueEditorProps {
  values: string[];
  placeholder: string;
  onAdd: (raw: string) => void;
  onClear: () => void;
}

function TextValueEditor({ values, placeholder, onAdd, onClear }: TextValueEditorProps) {
  const { t } = useI18n();
  const [draft, setDraft] = useState('');

  return (
    <div className="p-2 space-y-2">
      <input
        type="text"
        value={draft}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onAdd(draft);
            setDraft('');
          }
        }}
        className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
      />
      {values.length > 0 && (
        <p className="text-xs text-muted-foreground truncate">{values.join(', ')}</p>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => { onAdd(draft); setDraft(''); }}
          className="flex-1 rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          {t('workspace.filter.addValue')}
        </button>
        {values.length > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {t('common.clear')}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface FilterBarProps {
  filters: FilterValue[];
  availableFilters: FilterConfig[];
  onAddFilter: (filter: FilterValue) => void;
  onRemoveFilter: (field: string) => void;
  onClearAll: () => void;
  search: string;
  onSearchChange: (search: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const FilterBar = observer(({
  filters,
  availableFilters,
  onAddFilter,
  onRemoveFilter,
  onClearAll,
  search,
  onSearchChange,
}: FilterBarProps) => {
  const { t } = useI18n();

  // ---- Add-filter dropdown state ----
  const [addOpen, setAddOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<FilterConfig | null>(null);
  const addTriggerRef = useRef<HTMLButtonElement>(null);
  const addMenuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const updatePosition = useCallback(() => {
    if (!addTriggerRef.current) return;
    const rect = addTriggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const maxH = 320;
    const openAbove = spaceBelow < maxH && rect.top > spaceBelow;

    setMenuStyle({
      position: 'fixed',
      left: Math.min(rect.left, window.innerWidth - 240),
      width: 220,
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      maxHeight: Math.min(maxH, openAbove ? rect.top - 8 : spaceBelow - 8),
    });
  }, []);

  useEffect(() => {
    if (!addOpen) return;
    updatePosition();

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        addTriggerRef.current && !addTriggerRef.current.contains(target) &&
        addMenuRef.current && !addMenuRef.current.contains(target)
      ) {
        setAddOpen(false);
        setSelectedField(null);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setAddOpen(false);
        setSelectedField(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [addOpen, updatePosition]);

  // ---- Helpers ----

  const activeFields = new Set(filters.map((f) => f.field));
  const hasFilters = filters.length > 0;

  // Every field drills into a second step: a checkbox list for enum fields, a
  // user picker for `user`, a from/to pair for `date-range`, free text
  // otherwise. No field ever adds an empty placeholder chip.
  const handleFieldSelect = (config: FilterConfig) => {
    setSelectedField(config);
  };

  const closeMenu = () => {
    setAddOpen(false);
    setSelectedField(null);
  };

  const handleUserSelect = (config: FilterConfig, userId: string) => {
    if (!userId) {
      onRemoveFilter(config.field);
    } else {
      onAddFilter({ field: config.field, operator: 'in', value: [userId], labelKey: config.labelKey });
    }
    closeMenu();
  };

  const handleDateRangeApply = (config: FilterConfig, from: string, to: string) => {
    if (!from && !to) {
      onRemoveFilter(config.field);
    } else {
      onAddFilter({
        field: config.field,
        operator: 'between',
        value: [from, to] as [string, string],
        labelKey: config.labelKey,
      });
    }
    closeMenu();
  };

  const handleTextAdd = (config: FilterConfig, raw: string) => {
    const next = raw.trim();
    if (!next) return;
    const existing = filters.find((f) => f.field === config.field);
    const values = existing && Array.isArray(existing.value) ? (existing.value as string[]) : [];
    if (values.includes(next)) return;
    onAddFilter({
      field: config.field,
      operator: 'in',
      value: [...values, next],
      labelKey: config.labelKey,
    });
  };

  const handleOptionSelect = (config: FilterConfig, optionValue: string) => {
    const existing = filters.find((f) => f.field === config.field);
    if (existing && existing.operator === 'in' && Array.isArray(existing.value)) {
      const values = existing.value as string[];
      const newValues = values.includes(optionValue)
        ? values.filter((v) => v !== optionValue)
        : [...values, optionValue];
      if (newValues.length === 0) {
        onRemoveFilter(config.field);
      } else {
        onAddFilter({ field: config.field, operator: 'in', value: newValues, labelKey: config.labelKey });
      }
    } else {
      onAddFilter({ field: config.field, operator: 'in', value: [optionValue], labelKey: config.labelKey });
    }
  };

  const getFilterDisplayValue = (filter: FilterValue): string => {
    if (filter.operator === 'between' && Array.isArray(filter.value)) {
      const [from, to] = filter.value as [string, string];
      if (from && to) return `${from} → ${to}`;
      return from ? `≥ ${from}` : `≤ ${to}`;
    }
    if (Array.isArray(filter.value)) {
      const values = filter.value as string[];
      if (values.length <= 2) {
        return values.map((v) => {
          // Try to find a translated label from the config
          const config = availableFilters.find((f) => f.field === filter.field);
          const opt = config?.options?.find((o) => o.value === v);
          return opt ? t(opt.labelKey) : v;
        }).join(', ');
      }
      return t('workspace.filter.countValues', { count: values.length });
    }
    return String(filter.value);
  };

  const isOptionSelected = (config: FilterConfig, optionValue: string): boolean => {
    const existing = filters.find((f) => f.field === config.field);
    if (!existing) return false;
    if (Array.isArray(existing.value)) {
      return (existing.value as string[]).includes(optionValue);
    }
    return existing.value === optionValue;
  };

  // ---- Render ----

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Search input */}
      <div className="relative flex-shrink-0">
        <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('workspace.search.placeholder')}
          aria-label={t('workspace.search.placeholder')}
          className="h-8 w-48 sm:w-56 rounded-md border border-border bg-background pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 focus:ring-offset-background"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            aria-label={t('workspace.search.clear')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <XIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Active filter chips */}
      {filters.map((filter) => {
        const config = availableFilters.find((f) => f.field === filter.field);
        const label = config ? t(config.labelKey) : filter.field;
        const displayValue = getFilterDisplayValue(filter);

        return (
          <span
            key={filter.field}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-1 text-xs font-medium text-foreground"
          >
            <FilterIcon className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            <span className="text-muted-foreground">{label}:</span>
            <span className="max-w-[120px] truncate">{displayValue}</span>
            <button
              type="button"
              onClick={() => onRemoveFilter(filter.field)}
              aria-label={t('workspace.filter.remove', { field: label })}
              className="ml-0.5 rounded-sm text-muted-foreground hover:text-foreground hover:bg-accent/50 p-0.5"
            >
              <XIcon className="h-3 w-3" />
            </button>
          </span>
        );
      })}

      {/* Add filter button */}
      {availableFilters.length > 0 && (
        <button
          ref={addTriggerRef}
          type="button"
          onClick={() => {
            setAddOpen((prev) => !prev);
            setSelectedField(null);
          }}
          aria-haspopup="menu"
          aria-expanded={addOpen}
          className={cn(
            'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors',
            'text-muted-foreground hover:bg-accent/50 hover:text-foreground border border-dashed border-border',
            addOpen && 'bg-accent text-accent-foreground',
          )}
        >
          <PlusIcon className="h-3.5 w-3.5" />
          <span>{t('workspace.filter.add')}</span>
        </button>
      )}

      {/* Clear all button */}
      {hasFilters && (
        <button
          type="button"
          onClick={onClearAll}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          <XIcon className="h-3 w-3" />
          <span>{t('workspace.filter.clearAll')}</span>
        </button>
      )}

      {/* Add filter dropdown */}
      {addOpen && createPortal(
        <div
          ref={addMenuRef}
          role="menu"
          aria-label={t('workspace.filter.add')}
          style={menuStyle}
          className="z-50 overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-lg p-1"
        >
          {selectedField === null ? (
            // Field selection
            availableFilters.map((config) => (
              <button
                key={config.field}
                role="menuitem"
                className={cn(
                  'flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-left transition-colors',
                  activeFields.has(config.field)
                    ? 'text-muted-foreground'
                    : 'text-foreground hover:bg-accent/50',
                )}
                onClick={() => handleFieldSelect(config)}
              >
                <FilterIcon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate">{t(config.labelKey)}</span>
                {activeFields.has(config.field) && (
                  <CheckIcon className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
                )}
              </button>
            ))
          ) : (
            // Option selection for the chosen field
            <>
              <button
                type="button"
                className="flex items-center gap-1 w-full px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground rounded-md transition-colors"
                onClick={() => setSelectedField(null)}
              >
                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                <span>{t(selectedField.labelKey)}</span>
              </button>
              <div className="h-px bg-border my-1" />

              {selectedField.options && selectedField.options.length > 0 ? (
                selectedField.options.map((option) => (
                  <button
                    key={option.value}
                    role="menuitem"
                    className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-left transition-colors text-foreground hover:bg-accent/50"
                    onClick={() => handleOptionSelect(selectedField, option.value)}
                  >
                    <span className={cn(
                      'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border',
                      isOptionSelected(selectedField, option.value)
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border',
                    )}>
                      {isOptionSelected(selectedField, option.value) && (
                        <CheckIcon className="h-3 w-3" />
                      )}
                    </span>
                    <span className="flex-1 truncate">{t(option.labelKey)}</span>
                  </button>
                ))
              ) : selectedField.type === 'user' ? (
                <div className="p-2">
                  <UserPicker
                    value={firstValue(filters, selectedField.field)}
                    onChange={(userId) => handleUserSelect(selectedField, userId)}
                    placeholder={t('workspace.filter.selectUser')}
                  />
                </div>
              ) : selectedField.type === 'date-range' || selectedField.type === 'date' ? (
                <DateRangeEditor
                  initial={rangeValue(filters, selectedField.field)}
                  onApply={(from, to) => handleDateRangeApply(selectedField, from, to)}
                  onClear={() => { onRemoveFilter(selectedField.field); closeMenu(); }}
                />
              ) : (
                <TextValueEditor
                  values={arrayValue(filters, selectedField.field)}
                  placeholder={t('workspace.filter.valuePlaceholder')}
                  onAdd={(raw) => handleTextAdd(selectedField, raw)}
                  onClear={() => { onRemoveFilter(selectedField.field); closeMenu(); }}
                />
              )}
            </>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
});

FilterBar.displayName = 'FilterBar';
